import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select,
    Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { MerchReportService } from '../../services/merchReport.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { ISampleWorkloadFilters, ISampleWorkloadRow } from '../../interfaces/garments/merchReport.interface';
import { SampleType } from '../../interfaces/garments/sampleRequest.interface';
import { EmployeeAutocomplete, EmployeeOption, employeeLabelWithFullName } from '../common/EmployeeAutocomplete';
import { SAMPLE_TYPES } from './sampleTypeOptions';
import { money, triggerDownload } from '../statutoryRegisters/shared';
import { enumLabel } from '../../utils/garmentsLabels';

const toLocalIsoDate = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const firstOfMonth = (): string => {
    const now = new Date();
    return toLocalIsoDate(new Date(now.getFullYear(), now.getMonth(), 1));
};

const today = (): string => toLocalIsoDate(new Date());

/**
 * Sample-room workload &amp; cost report (8.4, spec
 * `docs/decisions/2026-09-11-garments-parties-buyer-compliance-sample-workload.md` §2). Filter
 * bar over `MerchandisingReportController#getSampleWorkload` — `from`/`to` are required by the
 * backend so they default to the current month; one row per assignee (or "Unassigned") x sample
 * type, plus a bold totals row. Mirrors `OrderDashboard`'s structure and permission gate
 * (`MERCH_REPORT_VIEW`, enforced by the route's `PermissionRoute`).
 */
export function SampleWorkloadReport() {
    const toast = useToast();

    const [rows, setRows] = useState<ISampleWorkloadRow[]>([]);
    const [totals, setTotals] = useState<ISampleWorkloadRow | null>(null);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    const [from, setFrom] = useState(firstOfMonth());
    const [to, setTo] = useState(today());
    const [assignee, setAssignee] = useState<EmployeeOption | null>(null);
    const [sampleType, setSampleType] = useState<SampleType | ''>('');

    const buildFilters = useCallback((): ISampleWorkloadFilters => ({
        from,
        to,
        assignedToUserId: assignee?.id || undefined,
        sampleType: sampleType || undefined,
    }), [from, to, assignee, sampleType]);

    const [appliedFilters, setAppliedFilters] = useState<ISampleWorkloadFilters>(buildFilters);

    const load = useCallback(async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        setLoading(true);
        const filters = buildFilters();
        try {
            const data = await MerchReportService.getSampleWorkload(institutionId, filters);
            setRows(data.rows);
            setTotals(data.totals);
            setTruncated(data.truncated);
            setAppliedFilters(filters);
        } catch (e) {
            toast.showApiError(e, 'Failed to load sample workload');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildFilters]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const clearFilters = () => {
        setFrom(firstOfMonth());
        setTo(today());
        setAssignee(null);
        setSampleType('');
    };

    const handleExport = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId) return;
        setDownloading(true);
        try {
            const blob = await MerchReportService.downloadSampleWorkloadXlsx(institutionId, appliedFilters);
            triggerDownload(blob, `sample-workload-${appliedFilters.from}-${appliedFilters.to}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Failed to export sample workload');
        } finally {
            setDownloading(false);
        }
    };

    const renderRow = (row: ISampleWorkloadRow, key: string, bold = false) => (
        <TableRow key={key} hover={!bold} data-testid={key} sx={bold ? { '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' } } : undefined}>
            <TableCell>{row.assigneeName}</TableCell>
            <TableCell>{enumLabel(row.sampleType)}</TableCell>
            <TableCell align="right">{row.open}</TableCell>
            <TableCell align="right">{row.overdue}</TableCell>
            <TableCell align="right">{row.dueWithin7Days}</TableCell>
            <TableCell align="right">{row.sentInPeriod}</TableCell>
            <TableCell align="right">{row.approvedInPeriod}</TableCell>
            <TableCell align="right">{row.rejectedInPeriod}</TableCell>
            <TableCell align="right">{row.avgTurnaroundDays ?? '—'}</TableCell>
            <TableCell align="right">{row.totalQuantity}</TableCell>
            <TableCell align="right">{money(row.totalCost)}</TableCell>
            <TableCell align="right">{row.totalLaborHours ?? '—'}</TableCell>
            <TableCell align="right">{money(row.costPerSample)}</TableCell>
        </TableRow>
    );

    return (
        <Box sx={{ p: 3 }} data-help-page="merchandising.sampleWorkload.page">
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">Sample Workload</Typography>
                <Button
                    data-help="merchandising.sampleWorkload.export"
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleExport} disabled={loading || downloading}
                >
                    Download
                </Button>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-help-section="merchandising.sampleWorkload.filter">
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <TextField
                        data-help="merchandising.sampleWorkload.from" size="small" label="From" type="date" InputLabelProps={{ shrink: true }}
                        value={from} onChange={e => setFrom(e.target.value)}
                        sx={{ flex: '1 1 200px', maxWidth: 220 }}
                    />
                    <TextField
                        data-help="merchandising.sampleWorkload.to" size="small" label="To" type="date" InputLabelProps={{ shrink: true }}
                        value={to} onChange={e => setTo(e.target.value)}
                        sx={{ flex: '1 1 200px', maxWidth: 220 }}
                    />
                    <Box sx={{ flex: '1 1 200px', maxWidth: 260 }}>
                        <EmployeeAutocomplete
                            data-help="merchandising.sampleWorkload.assignee"
                            value={assignee}
                            onChange={setAssignee}
                            label="Assignee"
                            size="small"
                            getOptionLabel={employeeLabelWithFullName}
                        />
                    </Box>
                    <FormControl data-help="merchandising.sampleWorkload.type" size="small" sx={{ flex: '1 1 200px', maxWidth: 200 }}>
                        <InputLabel>Sample Type</InputLabel>
                        <Select
                            label="Sample Type" value={sampleType}
                            onChange={e => setSampleType(e.target.value as SampleType | '')}
                        >
                            <MenuItem value="">All</MenuItem>
                            {SAMPLE_TYPES.map(t => <MenuItem key={t} value={t}>{enumLabel(t)}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Button data-help="merchandising.sampleWorkload.apply" variant="outlined" size="small" onClick={load} disabled={loading}>Apply</Button>
                    <Button data-help="merchandising.sampleWorkload.clear" size="small" onClick={clearFilters}>Clear</Button>
                </Box>
            </Paper>

            {truncated && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Showing a truncated set of sample requests — narrow the filters to see the rest.
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper} data-testid="sample-workload-table" data-help-section="merchandising.sampleWorkload.table">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell data-help="merchandising.sampleWorkload.col.assignee">Assignee</TableCell>
                                <TableCell data-help="merchandising.sampleWorkload.col.type">Type</TableCell>
                                <TableCell align="right" data-help="merchandising.sampleWorkload.col.open">Open</TableCell>
                                <TableCell align="right" data-help="merchandising.sampleWorkload.col.overdue">Overdue</TableCell>
                                <TableCell align="right" data-help="merchandising.sampleWorkload.col.due7">Due ≤7d</TableCell>
                                <TableCell align="right" data-help="merchandising.sampleWorkload.col.sent">Sent</TableCell>
                                <TableCell align="right" data-help="merchandising.sampleWorkload.col.approved">Approved</TableCell>
                                <TableCell align="right" data-help="merchandising.sampleWorkload.col.rejected">Rejected</TableCell>
                                <TableCell align="right" data-help="merchandising.sampleWorkload.col.turnaround">Avg Turnaround (d)</TableCell>
                                <TableCell align="right" data-help="merchandising.sampleWorkload.col.qty">Qty</TableCell>
                                <TableCell align="right" data-help="merchandising.sampleWorkload.col.cost">Cost</TableCell>
                                <TableCell align="right" data-help="merchandising.sampleWorkload.col.hours">Hours</TableCell>
                                <TableCell align="right" data-help="merchandising.sampleWorkload.col.costPerSample">Cost/Sample</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={13} align="center">
                                        <Typography color="text.secondary" sx={{ py: 4 }}>
                                            No sample requests match this filter.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {rows.map((row, i) => renderRow(row, `workload-row-${row.assignedToUserId ?? 'unassigned'}-${row.sampleType ?? 'none'}-${i}`))}
                            {totals && renderRow(totals, 'workload-totals', true)}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

export default SampleWorkloadReport;
